import { Rect } from '../lib/region';
import { DPR } from '../render/constants';
import { Action } from './action';

function condition() {
  return true;
}

export const zoomToFitAction = new Action({
  name: 'Zoom to fit',
  handler: zoomToFit,
  shortcuts: 'Shift+!',
  condition: condition,
});

export const zoomToSelectionAction = new Action({
  name: 'Zoom to selection',
  handler: zoomToSelection,
  shortcuts: 'Shift+@',
  condition: condition,
});

export const zoomOutAction = new Action({
  name: 'Zoom out',
  handler: zoomOut,
  shortcuts: '-',
  condition: condition,
});

export const zoomInAction = new Action({
  name: 'Zoom in',
  handler: zoomIn,
  shortcuts: '=',
  condition: condition,
});

export const zoomIntoAction = new Action({
  name: 'Zoom into',
  handler: (context) => {
    const nodes = context.api.getSelectedNodes();
    const node = nodes[0];
    scrollToNode(context, node, 60, true);
  },
  shortcuts: 'Shift+#',
  condition: condition,
});

export function zoomToFit(stageContext, padding = 200, canvas) {
  const nodes = stageContext.api.getNodes();
  if (nodes.length === 0) {
    stageContext.providers.scale.setScaleRaw(2, {
      x: 0,
      y: 0,
    });
    return;
  }
  zoomToNodes(stageContext, nodes, padding, canvas);
}

export function zoomToSelection(stageContext, padding = 40, animate) {
  const nodes = stageContext.api.getSelectedNodes();
  zoomToNodes(stageContext, nodes, padding, null, animate);
}

export function zoomToNodes(stageContext, nodes, padding = 40, canvas, animate) {
  if (nodes.length === 0) return;
  const boxes = nodes.map((node) => stageContext.api.getNodeBox(node));
  const firstNode = boxes[0];
  const rect = Rect.from(firstNode);

  boxes.forEach((box) => {
    const newRect = Rect.from(box);
    rect.expandRect(newRect);
  });

  rect.x -= padding;
  rect.y -= padding;
  rect.w += padding * 2;
  rect.h += padding * 2;

  canvas = canvas || stageContext.canvas.stage;
  const isPresentMode = stageContext.mode === 'presentation';
  const canvasWidth = isPresentMode ? canvas.width : canvas.width - stageContext.paddingRight * DPR;
  const canvasRect = new Rect(0, 0, canvasWidth, canvas.height);

  const scale = Math.min(canvasRect.w / rect.w, canvasRect.h / rect.h);
  const offsetX = (canvasRect.w - rect.w * scale) / 2;
  const offsetY = (canvasRect.h - rect.h * scale) / 2;
  const scaleProvider = stageContext.providers.scale;

  if (animate) {
    zoomAnimationByOffset(scaleProvider, scale, {
      x: offsetX - rect.x * scale,
      y: offsetY - rect.y * scale,
    });
  } else {
    scaleProvider.setScaleRaw(scale, {
      x: offsetX - rect.x * scale,
      y: offsetY - rect.y * scale,
    });
  }
}

export function scrollToNode(stageContext, node, padding = 40, animate = true) {
  if (!node) return;

  const box = stageContext.api.getNodeBox(node);
  const rect = Rect.from(box);

  // Add padding around the node
  rect.x -= padding;
  rect.y -= padding;
  rect.w += padding * 2;
  rect.h += padding * 2;

  const canvas = stageContext.canvas.stage;
  const isPresentMode = stageContext.mode === 'presentation';
  const canvasWidth = isPresentMode ? canvas.width : canvas.width - stageContext.paddingRight * DPR;
  const canvasRect = new Rect(0, 0, canvasWidth, canvas.height);

  const scaleProvider = stageContext.providers.scale;
  const currentScale = scaleProvider.scale;

  // Calculate the center position of the node in screen coordinates
  const centerX = rect.x + rect.w / 2;
  const centerY = rect.y + rect.h / 2;

  // Calculate the target offset to center the node on screen
  const targetOffsetX = canvasRect.w / 2 - centerX * currentScale;
  const targetOffsetY = canvasRect.h / 2 - centerY * currentScale;

  if (animate) {
    smoothScrollAnimation(scaleProvider, currentScale, {
      x: targetOffsetX,
      y: targetOffsetY,
    });
  } else {
    scaleProvider.setScaleRaw(currentScale, {
      x: targetOffsetX,
      y: targetOffsetY,
    });
  }
}

export function scrollToNodes(stageContext, nodes, padding = 40, animate = true) {
  if (nodes.length === 0) return;

  const boxes = nodes.map((node) => stageContext.api.getNodeBox(node));
  const firstNode = boxes[0];
  const rect = Rect.from(firstNode);

  boxes.forEach((box) => {
    const newRect = Rect.from(box);
    rect.expandRect(newRect);
  });

  // Add padding around the combined rect
  rect.x -= padding;
  rect.y -= padding;
  rect.w += padding * 2;
  rect.h += padding * 2;

  const canvas = stageContext.canvas.stage;
  const isPresentMode = stageContext.mode === 'presentation';
  const canvasWidth = isPresentMode ? canvas.width : canvas.width - stageContext.paddingRight * DPR;
  const canvasRect = new Rect(0, 0, canvasWidth, canvas.height);

  const scaleProvider = stageContext.providers.scale;
  const currentScale = scaleProvider.scale;

  // Calculate the center position of the nodes in screen coordinates
  const centerX = rect.x + rect.w / 2;
  const centerY = rect.y + rect.h / 2;

  // Calculate the target offset to center the nodes on screen
  const targetOffsetX = canvasRect.w / 2 - centerX * currentScale;
  const targetOffsetY = canvasRect.h / 2 - centerY * currentScale;

  if (animate) {
    smoothScrollAnimation(scaleProvider, currentScale, {
      x: targetOffsetX,
      y: targetOffsetY,
    });
  } else {
    scaleProvider.setScaleRaw(currentScale, {
      x: targetOffsetX,
      y: targetOffsetY,
    });
  }
}

export function zoomIn(stageContext) {
  zoomAnimation(stageContext, 1.2);
}

export function zoomOut(stageContext) {
  zoomAnimation(stageContext, 0.8);
}

let runningAnimation;

const zoomAnimation = (stageContext, v) => {
  let currentStep = 0;
  const totalSteps = 32;
  let stop = false;
  const scaleProvider = stageContext.providers.scale;
  const startZoom = scaleProvider.zoomValue;
  const targetZoom = startZoom * v;

  if (runningAnimation) runningAnimation();

  function updateScale() {
    currentStep++;

    if (currentStep >= totalSteps) {
      // Final step - set exact target value
      scaleProvider.setZoom(targetZoom);
      runningAnimation = null;
      return;
    }

    // Calculate smooth easing progress (ease-out cubic)
    const progress = 1 - Math.pow(1 - currentStep / totalSteps, 3);
    const newZoom = startZoom + (targetZoom - startZoom) * progress;

    scaleProvider.setZoom(newZoom);

    if (stop) return;
    requestAnimationFrame(updateScale);
  }

  updateScale();

  runningAnimation = () => {
    stop = true;
  };
};

let runningAnimationOffset;
const zoomAnimationByOffset = (scaleProvider, value, offset) => {
  let step = 0;
  let stop = false;
  if (runningAnimationOffset) runningAnimationOffset();
  const currentZoom = scaleProvider.scale;
  const currentX = scaleProvider.translateX;
  const currentY = scaleProvider.translateY;
  const diff = value - currentZoom;
  const diffX = offset.x - currentX;
  const diffY = offset.y - currentY;

  function updateScale() {
    step += 1;
    if (step === 10) {
      scaleProvider.setScaleRaw(value, offset);
      runningAnimationOffset = null;
      return;
    }
    const stepValue = step / 10;
    const newZoom = currentZoom + diff * stepValue;
    const newX = currentX + diffX * stepValue;
    const newY = currentY + diffY * stepValue;
    scaleProvider.setScaleRaw(newZoom, {
      x: newX,
      y: newY,
    });
    if (stop) return;
    requestAnimationFrame(updateScale);
  }
  updateScale();
  runningAnimationOffset = () => {
    stop = true;
    cancelAnimationFrame(runningAnimationOffset);
  };
};

let runningScrollAnimation;
const smoothScrollAnimation = (scaleProvider, scale, targetOffset) => {
  let step = 0;
  const totalSteps = 30;
  let stop = false;

  if (runningScrollAnimation) runningScrollAnimation();

  const currentX = scaleProvider.translateX;
  const currentY = scaleProvider.translateY;
  const diffX = targetOffset.x - currentX;
  const diffY = targetOffset.y - currentY;

  function updatePosition() {
    step += 1;

    if (step >= totalSteps) {
      scaleProvider.setScaleRaw(scale, targetOffset);
      runningScrollAnimation = null;
      return;
    }

    // Ease-out cubic easing
    const progress = 1 - Math.pow(1 - step / totalSteps, 3);
    const newX = currentX + diffX * progress;
    const newY = currentY + diffY * progress;

    scaleProvider.setScaleRaw(scale, {
      x: newX,
      y: newY,
    });

    if (stop) return;
    requestAnimationFrame(updatePosition);
  }

  updatePosition();

  runningScrollAnimation = () => {
    stop = true;
  };
};
